import { useEffect, useState, useCallback, useRef } from "react";
import { Star, Loader2, Camera, X } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

const Stars = ({ value, size = 16, onSelect }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={!onSelect}
        onClick={() => onSelect && onSelect(n)}
        data-testid={onSelect ? `star-select-${n}` : undefined}
        className={onSelect ? "cursor-pointer" : "cursor-default"}
      >
        <Star size={size} className={n <= value ? "fill-amber-400 text-amber-400" : "text-ink/20"} />
      </button>
    ))}
  </div>
);

export const Reviews = ({ productId }) => {
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (photos.length >= 3) { toast.error("Max 3 photos per review"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large — max 5MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/reviews/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, data.path]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed");
    } finally { setUploading(false); }
  };

  const load = useCallback(() => {
    api.get(`/products/${productId}/reviews`).then((r) => setData(r.data)).catch(() => {});
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    setSubmitting(true);
    try {
      await api.post(`/products/${productId}/reviews`, { name, rating, comment, website, photos });
      toast.success("Thanks for your review!");
      setName(""); setComment(""); setRating(5); setPhotos([]);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mt-16 border-t border-ink/10 pt-12" data-testid="reviews-section">
      <div className="flex flex-wrap items-center gap-4 mb-8">
        <h2 className="font-display text-2xl font-black tracking-tighter">Reviews</h2>
        {data.count > 0 && (
          <div className="flex items-center gap-2 text-sm" data-testid="reviews-summary">
            <Stars value={Math.round(data.average)} />
            <span className="font-bold">{data.average}</span>
            <span className="text-muted-foreground">({data.count} review{data.count === 1 ? "" : "s"})</span>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-10">
        <div className="space-y-6" data-testid="reviews-list">
          {data.reviews.length === 0 && (
            <p className="text-muted-foreground text-sm" data-testid="reviews-empty">
              No reviews yet — be the first to review this product.
            </p>
          )}
          {data.reviews.map((r) => (
            <div key={r.id} className="border border-ink/10 p-4 rounded-md" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between mb-1.5">
                <p className="font-bold text-sm">{r.name}</p>
                <Stars value={r.rating} size={14} />
              </div>
              {r.comment && <p className="text-sm text-muted-foreground leading-relaxed">{r.comment}</p>}
              {r.photos?.length > 0 && (
                <div className="flex gap-2 mt-2" data-testid={`review-photos-${r.id}`}>
                  {r.photos.map((p) => (
                    <a key={p} href={`${API}/files/${p}`} target="_blank" rel="noreferrer">
                      <img src={`${API}/files/${p}`} alt="Customer" loading="lazy" className="w-16 h-16 object-cover rounded-md border border-ink/10 hover:opacity-80 transition-opacity" />
                    </a>
                  ))}
                </div>
              )}
              <p className="text-xs text-ink/40 mt-2">{new Date(r.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="bg-secondary p-6 rounded-md h-fit space-y-4" data-testid="review-form">
          <h3 className="font-display font-bold">Write a review</h3>
          <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off"
            className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" name="website" />
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Your rating</label>
            <div className="mt-1"><Stars value={rating} size={22} onSelect={setRating} /></div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="review-name-input"
              className="w-full border-b border-ink/30 focus:border-brand outline-none bg-transparent py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Review (optional)</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} data-testid="review-comment-input"
              className="w-full border border-ink/20 focus:border-brand outline-none p-2 mt-1 text-sm bg-white" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Photos (optional, up to 3)</label>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPickPhoto} data-testid="review-photo-input" />
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {photos.map((p) => (
                <div key={p} className="relative">
                  <img src={`${API}/files/${p}`} alt="" className="w-14 h-14 object-cover rounded-md border border-ink/10" />
                  <button type="button" onClick={() => setPhotos(photos.filter((x) => x !== p))}
                    className="absolute -top-1.5 -right-1.5 bg-ink text-white rounded-full p-0.5" data-testid="remove-photo-btn">
                    <X size={11} />
                  </button>
                </div>
              ))}
              {photos.length < 3 && (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="w-14 h-14 border-2 border-dashed border-ink/25 rounded-md flex items-center justify-center text-ink/50 hover:border-brand hover:text-brand transition-colors"
                  data-testid="add-photo-btn">
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={18} />}
                </button>
              )}
            </div>
          </div>
          <button type="submit" disabled={submitting || uploading} className="btn-primary w-full !py-2.5" data-testid="review-submit-btn">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : "Submit review"}
          </button>
        </form>
      </div>
    </div>
  );
};
